<template>
  <div class="shotmap-container">
    <div class="header">
      <div class="tab" :class="selectedFilter === 'home' ? 'tab-active' : 'tab-inactive'" @click="changeTeam('home')">Home</div>
      <div class="tab" :class="selectedFilter === 'away' ? 'tab-active' : 'tab-inactive'" @click="changeTeam('away')">Away</div>
    </div>
    <!-- MINI GOAL -->
    <div class="mini-goal-section">
      <svg viewBox="0 0 1000 95" class="mini-goal-svg" preserveAspectRatio="xMidYMid meet">
        <rect x="0" y="80" width="1000" height="75" fill="#437E27" />
        <g stroke="#ffffff" stroke-width="5.5" fill="none" opacity="0.4"><rect x="350" y="80" width="300" height="35" /></g>
        <g>
          <rect x="380" y="10" width="240" height="70" rx="4" fill="#fff" />
          <rect x="390" y="18" width="220" height="62" rx="2" fill="#f5f5f5" />
          <g stroke="#d5d5d5" stroke-width="1.5" opacity="0.6">
            <line v-for="i in 11" :key="'v'+i" :x1="390 + (i*20)" y1="18" :x2="390 + (i*20)" y2="80"/>
            <line v-for="j in 7" :key="'h'+j" x1="390" :y1="18 + (j*10)" x2="610" :y2="18 + (j*10)"/>
          </g>
          <rect x="380" y="10" width="6" height="70" fill="#1a1a1a"/>
          <rect x="614" y="10" width="6" height="70" fill="#1a1a1a"/>
          <rect x="380" y="10" width="240" height="6" fill="#1a1a1a"/>
        </g>
        <g v-if="miniBallPosition">
          <circle :cx="miniBallPosition.cx" :cy="miniBallPosition.cy" :r="miniBallPosition.outerRadius" fill="#ffffff" />
          <circle :cx="miniBallPosition.cx" :cy="miniBallPosition.cy" :r="miniBallPosition.innerRadius" :fill="miniBallPosition.color" stroke="#ffffff" stroke-width="2.5"/>
        </g>
      </svg>
    </div>

    <!-- HALF-PITCH -->
    <div class="pitch-container">
      <div class="pitch-border"></div>
      <!-- Penalty + Goal box -->
      <div class="penalty-box"></div>
      <div class="goal-box"></div>
      <div class="goal-line"></div>

      <svg class="half-arc-top" viewBox="0 0 100 50" preserveAspectRatio="none">
        <path
          d="M 30 45 A 20 20 0 0 1 70 45"
          stroke="white"
          stroke-width="1"
          fill="none"
          opacity="0.9"
        />
      </svg> 

      <!-- Half Circle -->
      <svg class="half-arc" viewBox="0 0 100 50" preserveAspectRatio="none">
        <path
          d="M 30 45 A 20 20 0 0 1 70 45"
          stroke="white"
          stroke-width="1"
          fill="none"
          opacity="0.9"
        />
      </svg>

      <!-- Trajectory -->
      <svg 
        v-if="currentShot.draw && currentShot.draw.goal" 
        class="trajectory-svg" 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none"
      >
        <path
          :d="trajectoryPath"
          class="trajectory-path"
          stroke="#1e3a8a"
          stroke-width="0.5"
          stroke-linecap="round"
          fill="none"
        />
      </svg>

      <!-- BLOCK PERP MARK (perpendicular at dashed end) -->
      <div
        v-if="blockImpact"
        class="block-line"
        :style="{
          left: blockImpact.x + '%',
          top: blockImpact.y + '%',
          transform: 'translate(-50%, -50%) rotate(' + (blockImpact.angle - 90) + 'deg)'
        }"
      ></div>

      <!-- Shot Markers -->
      <div
        v-for="(shot, idx) in filteredShots"
        :key="idx"
        class="shot-marker"
        :class="{ active: idx === currentIndex }"
        :style="{
          left: shot.draw.start.x + '%',
          top: shot.draw.start.y + '%',
          transform: 'translate(-50%, -50%)',
          width: idx === currentIndex ? '10px' : '10px',
          height: idx === currentIndex ? '10px' : '10px',
          backgroundColor: getShotColor(shot.shotType),
          opacity: idx === currentIndex ? 1 : 0.7,
          border: idx === currentIndex ? '2px solid #fff' : 'none',
          boxShadow: idx === currentIndex ? '0 0 0 2px #1e3a8a' : 'none'
        }"
        @click="currentIndex = idx"
      ></div>
    </div>

    <!-- Player Navigation -->
    <div class="navigation">
      <button class="nav-btn" @click="prevShot">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>

      <div class="player-info">
        <div class="player-avatar">{{ currentShot.player.shortName.charAt(0) }}</div>
        <span class="player-name">{{ currentShot.player.shortName }}</span>
        <span class="player-time">{{ currentShot.time }}'</span>
      </div>

      <button class="nav-btn" @click="nextShot">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-item">
        <div class="stat-label">xG</div>
        <div class="stat-value">{{ currentShot.xg.toFixed(3) }}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">xGOT</div>
        <div class="stat-value">{{ currentShot.xgot.toFixed(3) }}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">Outcome</div>
        <div class="stat-value">{{ capitalize(currentShot.shotType) }}</div>
      </div>
    </div>

    <!-- Details -->
    <div class="details-grid">
      <div class="detail-item">
        <div class="detail-label">Situation</div>
        <div class="detail-value">{{ capitalize(currentShot.situation) }}</div>
      </div>
      <div class="detail-item">
        <div class="detail-label">Shot type</div>
        <div class="detail-value">{{ capitalize(currentShot.bodyPart) }}</div>
      </div>
      <div class="detail-item">
        <div class="detail-label">Goal zone</div>
        <div class="detail-value">{{ capitalize(currentShot.goalMouthLocation) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Shotmap",
  props: {
    shotsData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentIndex: 0,
      selectedFilter: "away",
      animatingMiniGoal: false,
    };
  },
  computed: {
    filteredShots() {
      return this.shotsData.filter((s) =>
        this.selectedFilter === "home" ? s.isHome : !s.isHome
      );
    },
    currentShot() {
      return this.filteredShots[this.currentIndex] || this.filteredShots[0];
    },
   miniBallPosition() {
  const shot = this.currentShot;
  if (!shot?.draw?.goal) return null;

  const goalX = shot.draw.goal.x;
  const goalY = shot.draw.goal.y;

  // Mini net boundaries
  const netLeft = 390;
  const netRight = 610;
  const netTop = 18;   // real net inside
  const netBottom = 80;

  const netWidth = netRight - netLeft;
  const netHeight = netBottom - netTop;

  const shotType = (shot.shotType || "").toLowerCase();
  const isOnTarget = ["goal", "save"].includes(shotType);

  // Normalize pitch Y → mini-goal net Y
  const normalizedY = netTop + (goalY / 100) * netHeight;

  // Normalize pitch X → mini-goal net X
  const normalizedX = netLeft + (goalX / 100) * netWidth;

  const inside = goalX >= 25 && goalX <= 75; // inside posts

  if (inside && isOnTarget) {
    return {
      cx: normalizedX,
      cy: normalizedY,
      outerRadius: 10,
      innerRadius: 12,
      color: this.getShotColor(shotType),
    };
  }

  // OUTSIDE net → show at side
  return {
    cx: goalX < 50 ? netLeft - 40 : netRight + 40,
    cy: netTop + netHeight / 2,
    outerRadius: 10,
    innerRadius: 12,
    color: this.getShotColor(shotType),
  };
},

    /* BLOCK IMPACT: compute exact same midpoint + tangent as trajectory uses for BLOCK */
   blockImpact() {
  const s = this.currentShot;
  if (!s || !s.draw) return null;
  if ((s.shotType || "").toLowerCase() !== "block") return null;

  const loc = (s.goalMouthLocation || "").toLowerCase();
  let goalX = 50;
  if (loc.includes("left")) goalX = 42;
  else if (loc.includes("right")) goalX = 58;

  const start = s.draw.start;

  // mid point along trajectory
  const frac = 0.35;
  const midX = start.x + (goalX - start.x) * frac;
  const midY = start.y + (0 - start.y) * frac;

  const dx = midX - start.x;
  const dy = midY - start.y;

  const angle = Math.atan2(dy, dx) * 180 / Math.PI;

  // base perpendicular
  let perp = angle + 95;

  // dynamic tilt
  const magnitude = Math.min(25, Math.max(8, Math.abs(angle) * 0.35));
  perp += (dx >= 0 ? magnitude : -magnitude);

  // shift forward
  const shift = 0.23;
  const sx = midX + dx * shift;
  const sy = midY + dy * shift;

  return {
    x: sx,
    y: sy,
    angle: perp
  };
}
,
   trajectoryPath() {
      const s = this.currentShot;
      if (!s?.draw) return "";

      const start = {
        x: s.draw.start.x,
        y: s.draw.start.y
      };

      const type = (s.shotType || "").toLowerCase();
      const loc = (s.goalMouthLocation || "").toLowerCase();

      // Determine goal position based on goalMouthLocation
      let goalX = 50; // center by default
      let goalY = 2; // top of pitch (goal line)

      // Horizontal position based on goal mouth location
      if (loc.includes("left")) {
        goalX = 42; // left side of goal
      } else if (loc.includes("right")) {
        goalX = 58; // right side of goal
      } else if (loc.includes("centre") || loc.includes("center")) {
        goalX = 50; // center
      }

      // For misses, adjust the goal position based on draw.goal.x
      if (type === "miss") {
        if (s.draw.goal.x < 38) {
          goalX = 35; // wide left
        } else if (s.draw.goal.x > 62) {
          goalX = 65; // wide right
        }
      }

      // ---------- BLOCKED (short straight line) ----------
      if (type === "block") {
        const mid = {
          x: start.x + (goalX - start.x) * 0.45,
          y: start.y + (goalY - start.y) * 0.45
        };
        return `M ${start.x} ${start.y} L ${mid.x} ${mid.y}`;
      }

      // ---------- MISS / SAVE (straight to goal) ----------
      if (type !== "goal") {
        return `M ${start.x} ${start.y} L ${goalX} ${goalY}`;
      }

      // ---------- GOAL (curved path to goal) ----------
      const x1 = start.x;
      const y1 = start.y;
      const x2 = goalX;
      const y2 = goalY;

      // midpoint
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;

      // direction
      const dx = x2 - x1;
      const dy = y2 - y1;
      const dist = Math.hypot(dx, dy) || 1;

      // perpendicular vector
      let px = -dy / dist;
      let py = dx / dist;

      // Adjust curve to bend toward the goal (upward)
      const towardGoal = (50 - mx) * px + (0 - my) * py;
      if (towardGoal < 0) {
        px = -px;
        py = -py;
      }

      const bulge = Math.min(16, Math.max(6, dist * 0.14));

      const cx = mx + px * bulge;
      const cy = my + py * bulge;

      return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
    }
  },
  methods: {
    changeTeam(team) {
      this.selectedFilter = team;
      this.currentIndex = 0;
    },
    getShotColor(type) {
      return {
        goal: "#10b981",
        save: "#60a5fa",
        miss: "#94a3b8",
        block: "#f59e0b",
      }[type] || "#94a3b8";
    },
    capitalize(str) {
      return str
        ? str.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")
        : "";
    },
    nextShot() {
  this.animatingMiniGoal = false;
  this.currentIndex = (this.currentIndex + 1) % this.filteredShots.length;

  // Let DOM update then animate
  this.$nextTick(() => {
    setTimeout(() => (this.animatingMiniGoal = true), 20);
  });
},
    prevShot() {
  this.animatingMiniGoal = false;
  this.currentIndex =
    (this.currentIndex - 1 + this.filteredShots.length) %
    this.filteredShots.length;

  this.$nextTick(() => {
    setTimeout(() => (this.animatingMiniGoal = true), 20);
  });
},
  },
};
</script>

<style scoped>
.shotmap-container {
  max-width: 420px;
  margin: 0 auto;
  background: #fafafa;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
}

.tab {
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-active {
  background-color: #1a1a1a;
  color: white;
}

.tab-inactive {
  background-color: white;
  color: #666;
  border: 1px solid #ddd;
}

.mini-goal-section {
  text-align: center;
  padding: 0;
}

.mini-goal-svg {
  width: 100%;
  height: 95px;
  display: block;
}

.pitch-container {
  position: relative;
  height: 170px;
  background: #437E27;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  overflow: hidden;
}

.pitch-border {
  position: absolute;
  top: 0;
  left: 16px;
  right: 16px;
  height: 221px;
  border: 2px solid rgba(255,255,255,0.4);
  border-radius: 4px;
}

.penalty-box {
  position: absolute;
  left: 50%;
  top: 0;
  width: 220px;
  height: 80px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top: none;
  transform: translateX(-50%);
}

.goal-box {
  position: absolute;
  left: 50%;
  top: 0;
  width: 118px;
  height: 32px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top: none;
  transform: translateX(-50%);
}

.goal-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 76px;
  height: 4px;
  background: white;
  transform: translateX(-50%);
}

.half-arc {
  position: absolute;
    bottom: 4px;
    left: 26%;
    width: 50%;
    height: 65px;
    pointer-events: none;
    z-index: 1;
}

.half-arc-top {
position: absolute;
    top: 76px;
    left: 33%;
    width: 34%;
    height: 40px;
    pointer-events: none;
    z-index: 2;
    transform: rotate(180deg);
}

.trajectory-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.trajectory-path {
  stroke-width: 0.5 !important;
  stroke-dasharray: 3 3 !important;
  opacity: 0.85 !important;
}

/* block perpendicular mark: short horizontal bar + small vertical stub */
.block-line {
  position: absolute;
  width: 12px;         /* horizontal bar length */
  height: 1.6px;       /* thickness of horizontal bar */
  background: #1e3a8a;
  z-index: 5;
  opacity: 0.95;
  border-radius: 2px;
  pointer-events: none;
}
.block-line::after {
  content: "";
  position: absolute;
  right: -1px;
  top: -6px;
  width: 2px;
  height: 12px;
  background: #1e3a8a;
  border-radius: 1px;
}

.shot-marker {
  position: absolute;
  border-radius: 50%;
  cursor: pointer;
  transition: 0.3s;
  z-index: 3;
}

.shot-marker:hover {
  transform: translate(-50%, -50%) scale(1.1) !important;
}

.navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
  margin-bottom: 20px;
}

.nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.nav-btn:hover {
  background-color: #e0e0e0;
}

.player-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.player-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #fb923c, #f97316);
  color: white;
  font-weight: bold;
  font-size: 14px;
}

.player-name { font-weight: 500; color: #1a1a1a; font-size: 15px; }
.player-time { color: #666; margin-left: 8px; font-size: 14px; }

.stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; background: white; padding: 16px; border-radius: 8px; margin-bottom: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.stat-item { text-align: center; }
.stat-label { font-size: 11px; color: #666; margin-bottom: 4px; text-transform: uppercase; font-weight: 500; }
.stat-value { font-weight: 600; color: #1a1a1a; font-size: 14px; }

.details-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; background: white; padding: 16px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.detail-item { text-align: center; }
.detail-label { font-size: 11px; color: #666; margin-bottom: 4px; font-weight: 500; }
.detail-value { font-weight: 500; color: #1a1a1a; font-size: 12px; }
.mini-ball {
  transition: cx 0.45s ease-out, cy 0.45s ease-out, r 0.3s ease-out;
}
</style>
